// 📂 frontend/src/components/ScheduleEmailForm.tsx
import React, { useState, useEffect } from 'react';
import emailApi from '../services/emailApi';
import EmailEditor from './EmailEditor';
import { useToast } from '../hooks/useToast';

interface ScheduleEmailFormProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any; // ThemeColors from ThemeContext
  onSuccess: (emailData: Record<string, unknown>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editingEmail?: any; // Email type from parent
  onCancel?: () => void;
  onTemplateCreated?: () => void; // Callback pentru când se creează un template
  prefillContent?: string;
  prefillSubject?: string;
}

const ScheduleEmailForm: React.FC<ScheduleEmailFormProps> = ({
  theme,
  onSuccess,
  editingEmail,
  onCancel,
  onTemplateCreated,
  prefillContent,
  prefillSubject
}) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    scheduled_for: '',
    is_recurring: false,
    recurring_type: 'none',
    recurring_interval: 1, // NOUĂ - pentru intervale personalizate
    reminder_days: 0,
    priority: 'normal',
    email_type: 'transactional'
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // NOUĂ - previne dubluri
  const [error, setError] = useState('');
  const [sendNow, setSendNow] = useState(false);
  const [recipientsList, setRecipientsList] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false); // NOUĂ - pentru salvare template
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [attachments, setAttachments] = useState<Array<{filename: string, original_name: string}>>([]);
  const [contactGroups, setContactGroups] = useState<Array<{id: number, name: string, color: string}>>([]);

  // Populează formularul din Generator
  useEffect(() => {
    if (prefillContent) {
      setFormData(prev => ({
        ...prev,
        content: prefillContent,
        subject: prefillSubject || prev.subject
      }));
    }
  }, [prefillContent, prefillSubject]);

  // Populează formularul dacă editează
  useEffect(() => {
    if (editingEmail) {
      const scheduledDate = new Date(editingEmail.scheduledFor || new Date());
      const formattedDate = scheduledDate.toISOString().slice(0, 16);

      // Parse recipients
      const toValue = editingEmail.to || '';
      const recipients = Array.isArray(toValue)
        ? toValue
        : toValue.split(',').map((r: string) => r.trim());

      setRecipientsList(recipients);
      setFormData({
        subject: editingEmail.subject || '',
        content: editingEmail.content || '',
        scheduled_for: formattedDate,
        is_recurring: editingEmail.isRecurring || false,
        recurring_type: editingEmail.recurringType || 'none',
        recurring_interval: editingEmail.recurringInterval || 1,
        reminder_days: editingEmail.reminderDays || 0,
        priority: editingEmail.priority || 'normal',
        email_type: editingEmail.emailType || 'transactional'
      });
    }
  }, [editingEmail]);

  // Încarcă grupuri de contacte
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const groups = await emailApi.getGroups();
        setContactGroups(groups as Array<{id: number, name: string, color: string}>);
      } catch {}
    };
    loadGroups();
  }, []);

  // Adaugă destinatar
  const addRecipient = () => {
    const email = recipientInput.trim();
    if (!email) return;

    // Validare email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Email invalid', 'Introdu o adresă de email validă');
      return;
    }

    // Verifică duplicat
    if (recipientsList.includes(email)) {
      toast.warning('Duplicat', 'Acest email există deja în listă');
      return;
    }

    setRecipientsList([...recipientsList, email]);
    setRecipientInput('');
    toast.success('Adăugat', `${email} a fost adăugat în listă`);
  };

  // Șterge destinatar
  const removeRecipient = (email: string) => {
    setRecipientsList(recipientsList.filter(r => r !== email));
    toast.info('Eliminat', `${email} a fost eliminat din listă`);
  };

  // Import destinatari din CSV
  const importRecipientsFromText = () => {
    // TODO: Replace with custom modal
    const text = prompt('Lipește lista de email-uri (separate prin virgulă, spațiu sau linie nouă):');
    if (!text) return;

    const emails = text
      .split(/[,\s\n]+/)
      .map(e => e.trim())
      .filter(e => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

    const newEmails = emails.filter(e => !recipientsList.includes(e));
    setRecipientsList([...recipientsList, ...newEmails]);
    
    toast.success('Import', `${newEmails.length} email-uri adăugate`);
  };

  // Salvează ca template - VERSIUNEA CORECTATĂ cu anti-dubluri
  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Eroare', 'Introdu un nume pentru template');
      return;
    }

    // Previne apăsări multiple
    if (isSavingTemplate) {
      return;
    }

    setIsSavingTemplate(true);

    try {
      const response = await emailApi.createTemplate({
        name: templateName,
        subject: formData.subject,
        body: formData.content,
        content: formData.content,
        template_type: 'custom',
        category: 'Personal',
        tags: [],
        is_public: false
      });

      if (response && response.success) {
        toast.success('Succes', `Template "${templateName}" salvat cu succes!`);
        setShowSaveTemplate(false);
        setTemplateName('');
        
        // Apelează callback-ul pentru a reîncărca template-urile
        if (onTemplateCreated) {
          onTemplateCreated();
        }
      } else {
        throw new Error('Răspuns invalid de la server');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Eroare', 'Nu am putut salva template-ul');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (recipientsList.length === 0) {
      errors.recipients = 'Adaugă cel puțin un destinatar';
    }

    if (!formData.subject.trim()) {
      errors.subject = 'Subiectul este obligatoriu';
    }

    if (!formData.content.trim()) {
      errors.content = 'Conținutul este obligatoriu';
    }

    if (!sendNow && !formData.scheduled_for) {
      errors.scheduled_for = 'Selectează data și ora de programare';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const doSubmit = async (mode: 'new' | 'update' | 'reschedule') => {
    // Previne trimiteri multiple
    if (isSubmitting) {
      return;
    }

    setError('');
    setIsLoading(true);
    setIsSubmitting(true);

    // Validări
    if (!validateForm()) {
      setIsLoading(false);
      setIsSubmitting(false);
      return;
    }

    try {
      let response;
      const emailData: Record<string, unknown> = {
        to: recipientsList.join(', '),
        subject: formData.subject,
        content: formData.content,
        priority: formData.priority,
        email_type: formData.email_type,
        ...(cc && { cc }),
        ...(bcc && { bcc }),
        ...(attachments.length > 0 && { attachments: JSON.stringify(attachments.map(a => a.filename)) })
      };

      if (sendNow) {
        response = await emailApi.sendEmail(emailData as unknown as Parameters<typeof emailApi.sendEmail>[0]);
      } else if (mode === 'update' && editingEmail?.id) {
        // Salvează modificările pe emailul existent (fără a crea unul nou)
        const scheduledDate = new Date(formData.scheduled_for);
        const updateData = {
          ...emailData,
          scheduled_for: scheduledDate.toISOString(),
          is_recurring: formData.is_recurring,
          recurring_type: formData.recurring_type,
          recurring_interval: formData.recurring_interval,
          reminder_days: formData.reminder_days
        };
        response = await emailApi.updateEmail(editingEmail.id, updateData);
        toast.success('Salvat', 'Email-ul a fost actualizat cu succes');
      } else {
        // Programează nou (sau reprogramează = creare nouă)
        const scheduledDate = new Date(formData.scheduled_for);
        const scheduleData = {
          ...emailData,
          scheduled_for: scheduledDate.toISOString(),
          is_recurring: formData.is_recurring,
          recurring_type: formData.recurring_type,
          recurring_interval: formData.recurring_interval,
          reminder_days: formData.reminder_days
        };
        response = await emailApi.scheduleEmail(scheduleData as unknown as Parameters<typeof emailApi.scheduleEmail>[0]);
      }

      // Resetează formularul
      setFormData({
        subject: '',
        content: '',
        scheduled_for: '',
        is_recurring: false,
        recurring_type: 'none',
        recurring_interval: 1,
        reminder_days: 0,
        priority: 'normal',
        email_type: 'transactional'
      });
      setRecipientsList([]);
      setRecipientInput('');
      setSendNow(false);
      setCc('');
      setBcc('');
      setAttachments([]);

      if (onSuccess) {
        const resultData = {
          ...response.data,
          to: recipientsList.join(', '),
          subject: formData.subject,
          content: formData.content,
          status: sendNow ? 'sent' : 'scheduled',
          scheduledFor: sendNow ? new Date() : new Date(formData.scheduled_for),
          isRecurring: formData.is_recurring,
          recurringType: formData.recurring_type,
          recurringInterval: formData.recurring_interval,
          reminderDays: formData.reminder_days,
          priority: formData.priority,
          emailType: formData.email_type
        };
        onSuccess(resultData);
      }

    } catch (error: unknown) {
      console.error('Error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Eroare la procesarea email-ului';
      setError(errorMsg);
      toast.error('Eroare', errorMsg);
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setIsSubmitting(false);
      }, 1000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingEmail) {
      doSubmit('update');
    } else {
      doSubmit('new');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handler special pentru schimbarea frecvenței de recurență
  const handleRecurringChange = (value: string) => {
    if (value === 'none') {
      setFormData(prev => ({
        ...prev,
        recurring_type: 'none',
        recurring_interval: 1
      }));
    } else {
      const [type, interval] = value.split('-');
      setFormData(prev => ({
        ...prev,
        recurring_type: type,
        recurring_interval: parseInt(interval) || 1
      }));
    }
  };

  // Setează data minimă la data curentă + 1 minut
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 1);
    return now.toISOString().slice(0, 16);
  };

  // Obține valoarea curentă pentru select-ul de recurență
  const getRecurringValue = () => {
    if (formData.recurring_type === 'none' || !formData.recurring_type) {
      return 'none';
    }
    if (formData.recurring_type === 'monthly') {
      return `monthly-${formData.recurring_interval}`;
    }
    return formData.recurring_type;
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '20px' }}>
        {editingEmail ? '✏️ Editează Email' : '📧 Programează Email Nou'}
      </h2>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#ef4444'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Destinatari multipli */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Destinatari * ({recipientsList.length})
          </label>
          
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input
              type="email"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addRecipient();
                }
              }}
              placeholder="adresa@email.com"
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.background,
                color: theme.text,
                fontSize: '14px'
              }}
            />
            <button
              type="button"
              onClick={addRecipient}
              style={{
                padding: '12px 20px',
                backgroundColor: theme.primary,
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              ➕ Adaugă
            </button>
            <button
              type="button"
              onClick={importRecipientsFromText}
              style={{
                padding: '12px 20px',
                backgroundColor: 'transparent',
                color: theme.text,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                cursor: 'pointer'
              }}
              title="Import din text/CSV"
            >
              📥 Import
            </button>
          </div>

          {fieldErrors.recipients && (
            <p style={{ color: '#ef4444', fontSize: '13px', margin: '4px 0 0 0' }}>
              {fieldErrors.recipients}
            </p>
          )}

          {/* Lista destinatari */}
          {recipientsList.length > 0 && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              padding: '12px',
              backgroundColor: theme.surface,
              borderRadius: '8px',
              border: `1px solid ${theme.border}`
            }}>
              {recipientsList.map((email, index) => (
                <div
                  key={index}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 12px',
                    backgroundColor: theme.primary + '22',
                    borderRadius: '16px',
                    fontSize: '14px'
                  }}
                >
                  <span>{email}</span>
                  <button
                    type="button"
                    onClick={() => removeRecipient(email)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: theme.danger,
                      cursor: 'pointer',
                      padding: '0 4px',
                      fontSize: '16px'
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Quick-add grup de contacte */}
          {contactGroups.length > 0 && (
            <div style={{ marginTop: '6px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', alignSelf: 'center' }}>Adauga grup:</span>
              {contactGroups.map(g => (
                <button key={g.id} type="button" onClick={async () => {
                  try {
                    const emails = await emailApi.getGroupEmails(g.id);
                    const newRecipients = emails.filter((e: string) => !recipientsList.includes(e));
                    if (newRecipients.length > 0) setRecipientsList(prev => [...prev, ...newRecipients]);
                  } catch {}
                }} style={{
                  padding: '3px 8px', fontSize: '11px', border: 'none', borderRadius: '4px',
                  backgroundColor: g.color || '#3498db', color: 'white', cursor: 'pointer', opacity: 0.85
                }}>{g.name}</button>
              ))}
            </div>
          )}
        </div>

        {/* CC */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>CC (optional)</label>
          <input type="text" value={cc} onChange={e => setCc(e.target.value)} placeholder="cc1@example.com, cc2@example.com"
            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: `1px solid ${theme.border}`, backgroundColor: theme.background, color: theme.text, fontSize: '14px', boxSizing: 'border-box' }} />
        </div>

        {/* BCC */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>BCC (optional)</label>
          <input type="text" value={bcc} onChange={e => setBcc(e.target.value)} placeholder="bcc@example.com"
            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: `1px solid ${theme.border}`, backgroundColor: theme.background, color: theme.text, fontSize: '14px', boxSizing: 'border-box' }} />
        </div>

        {/* Atasamente */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>Atasamente</label>
          <input type="file" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > 10 * 1024 * 1024) { alert('Max 10MB'); return; }
            try {
              const result = await emailApi.uploadFile(file);
              if (result.success) setAttachments(prev => [...prev, result.data as {filename: string, original_name: string}]);
            } catch { /* silently fail */ }
          }} style={{ fontSize: '13px' }} />
          {attachments.length > 0 && (
            <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {attachments.map((a, i) => (
                <span key={i} style={{ fontSize: '12px', backgroundColor: '#e8f4fd', padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {'\uD83D\uDCCE'} {a.original_name}
                  <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontSize: '14px' }}>{'\u00D7'}</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Subiect */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Subiect *
          </label>
          <input
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="Subiectul email-ului"
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.background,
              color: theme.text,
              fontSize: '14px'
            }}
            required
          />
          {fieldErrors.subject && (
            <p style={{ color: '#ef4444', fontSize: '13px', margin: '4px 0 0 0' }}>
              {fieldErrors.subject}
            </p>
          )}
        </div>

        {/* Editor Conținut cu HTML și Preview */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Conținut *
          </label>
          <EmailEditor
            initialContent={formData.content}
            onChange={(content) => setFormData(prev => ({ ...prev, content }))}
            onSaveAsTemplate={() => setShowSaveTemplate(true)}
            showTemplateButton={true}
          />
          {fieldErrors.content && (
            <p style={{ color: '#ef4444', fontSize: '13px', margin: '4px 0 0 0' }}>
              {fieldErrors.content}
            </p>
          )}
        </div>

        {/* Opțiuni avansate */}
        <div style={{ 
          marginBottom: '20px',
          padding: '20px',
          backgroundColor: theme.surface,
          borderRadius: '8px',
          border: `1px solid ${theme.border}`
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>⚙️ Opțiuni Avansate</h3>
          
          {/* Data programare */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={sendNow}
                onChange={(e) => setSendNow(e.target.checked)}
              />
              <span>Trimite imediat</span>
            </label>
            
            {!sendNow && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                  Programează pentru:
                </label>
                <input
                  type="datetime-local"
                  name="scheduled_for"
                  value={formData.scheduled_for}
                  onChange={handleChange}
                  min={getMinDateTime()}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: `1px solid ${theme.border}`,
                    backgroundColor: theme.background,
                    color: theme.text,
                    fontSize: '14px'
                  }}
                  required={!sendNow}
                />
              </div>
            )}
          </div>

          <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Prioritate */}
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                Prioritate:
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.background,
                  color: theme.text,
                  fontSize: '14px'
                }}
              >
                <option value="low">🔵 Scăzută</option>
                <option value="normal">🟢 Normală</option>
                <option value="high">🟡 Înaltă</option>
                <option value="urgent">🔴 Urgentă</option>
              </select>
            </div>

            {/* Tip email */}
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                Tip email:
              </label>
              <select
                name="email_type"
                value={formData.email_type}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.background,
                  color: theme.text,
                  fontSize: '14px'
                }}
              >
                <option value="transactional">💼 Tranzacțional</option>
                <option value="marketing">📢 Marketing</option>
                <option value="notification">🔔 Notificare</option>
                <option value="newsletter">📰 Newsletter</option>
              </select>
            </div>
          </div>

          {/* Email recurent */}
          {!sendNow && (
            <div style={{ marginTop: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  name="is_recurring"
                  checked={formData.is_recurring}
                  onChange={handleChange}
                />
                <span>Email recurent</span>
              </label>

              {formData.is_recurring && (
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                      Frecvență:
                    </label>
                    <select
                      value={getRecurringValue()}
                      onChange={(e) => handleRecurringChange(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '6px',
                        border: `1px solid ${theme.border}`,
                        backgroundColor: theme.background,
                        color: theme.text,
                        fontSize: '14px'
                      }}
                    >
                      <option value="none">Nu repeta</option>
                      <option value="daily">Zilnic</option>
                      <option value="weekly">Săptămânal</option>
                      <option value="monthly-1">Lunar (1 luna)</option>
                      <option value="monthly-2">La 2 luni</option>
                      <option value="monthly-3">La 3 luni</option>
                      <option value="monthly-4">La 4 luni</option>
                      <option value="monthly-5">La 5 luni</option>
                      <option value="monthly-6">La 6 luni</option>
                      <option value="yearly">Anual</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                      Reminder (zile înainte):
                    </label>
                    <input
                      type="number"
                      name="reminder_days"
                      value={formData.reminder_days}
                      onChange={handleChange}
                      min="0"
                      max="30"
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '6px',
                        border: `1px solid ${theme.border}`,
                        backgroundColor: theme.background,
                        color: theme.text,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reamintire inainte de trimitere */}
          {!sendNow && (
            <div style={{ marginBottom: '12px', marginTop: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={formData.reminder_days > 0}
                  onChange={(e) => setFormData(prev => ({ ...prev, reminder_days: e.target.checked ? 1 : 0 }))}
                />
                <span>Reamintire inainte de trimitere</span>
              </label>
              {formData.reminder_days > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>Cu</span>
                  <select
                    value={formData.reminder_days}
                    onChange={(e) => setFormData(prev => ({ ...prev, reminder_days: parseInt(e.target.value) }))}
                    style={{
                      padding: '6px 10px', borderRadius: '6px',
                      border: '1px solid var(--border-color, #334155)',
                      backgroundColor: 'var(--input-bg, #0f172a)', color: 'var(--text-primary, #e2e8f0)',
                      fontSize: '13px'
                    }}
                  >
                    {[1, 2, 3, 5, 7, 14, 30].map(d => (
                      <option key={d} value={d}>{d} {d === 1 ? 'zi' : 'zile'}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>inainte</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Butoane */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {editingEmail ? (
            <>
              <button
                type="submit"
                disabled={isLoading || isSubmitting}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: isSubmitting ? theme.success : theme.primary,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: (isLoading || isSubmitting) ? 'not-allowed' : 'pointer',
                  opacity: (isLoading || isSubmitting) ? 0.6 : 1,
                  transition: 'all 0.3s ease'
                }}
              >
                {isSubmitting ? '✓ Salvat!' : isLoading ? '⏳ Se salvează...' : '💾 Salvează Modificările'}
              </button>
              <button
                type="button"
                disabled={isLoading || isSubmitting}
                onClick={() => doSubmit('reschedule')}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: isSubmitting ? theme.success : '#f59e0b',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: (isLoading || isSubmitting) ? 'not-allowed' : 'pointer',
                  opacity: (isLoading || isSubmitting) ? 0.6 : 1,
                  transition: 'all 0.3s ease'
                }}
              >
                {isLoading ? '⏳ Se procesează...' : '🔄 Salvează și Reprogramează'}
              </button>
            </>
          ) : (
            <button
              type="submit"
              disabled={isLoading || isSubmitting}
              style={{
                flex: 1,
                padding: '12px 24px',
                backgroundColor: isSubmitting ? theme.success : theme.primary,
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: (isLoading || isSubmitting) ? 'not-allowed' : 'pointer',
                opacity: (isLoading || isSubmitting) ? 0.6 : 1,
                transition: 'all 0.3s ease'
              }}
            >
              {isSubmitting ? '✓ Trimis!' : isLoading ? '⏳ Se procesează...' : sendNow ? '📤 Trimite Acum' : '📅 Programează'}
            </button>
          )}
          
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                backgroundColor: 'transparent',
                color: theme.text,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.6 : 1
              }}
            >
              Anulează
            </button>
          )}
        </div>
      </form>

      {/* Modal salvare ca template */}
      {showSaveTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowSaveTemplate(false)}>
          <div style={{
            backgroundColor: theme.surface,
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '400px',
            width: '100%'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '20px' }}>💾 Salvează ca Template</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Nume template:
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="ex: Email de bun venit"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.background,
                  color: theme.text
                }}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && templateName.trim()) {
                    saveAsTemplate();
                  }
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={saveAsTemplate}
                disabled={!templateName.trim() || isSavingTemplate}
                style={{
                  flex: 1,
                  padding: '10px 20px',
                  backgroundColor: theme.primary,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (templateName.trim() && !isSavingTemplate) ? 'pointer' : 'not-allowed',
                  opacity: (templateName.trim() && !isSavingTemplate) ? 1 : 0.6
                }}
              >
                {isSavingTemplate ? '⏳ Se salvează...' : 'Salvează'}
              </button>
              <button
                onClick={() => {
                  setShowSaveTemplate(false);
                  setTemplateName('');
                }}
                disabled={isSavingTemplate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '8px',
                  cursor: isSavingTemplate ? 'not-allowed' : 'pointer',
                  opacity: isSavingTemplate ? 0.6 : 1
                }}
              >
                Anulează
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleEmailForm;